import React, { useEffect, useState } from 'react';
import { connect } from 'dva';
import PropTypes from 'prop-types';
import Switch from 'react-switch';
import { LIMITED_ACCESS_ROLES, MAP_LAYER_IDS, MAP_MODES, TERRAIN_BANDS } from '../../../lib/constants';
import actions from './actions';
import AspectFilter from './AspectFilter';
import Button from '../../Button';
import ElevationPanel from './ElevationFilter';
import SidebarBody from '../SidebarBody';
import SidebarDetailHeader from '../SidebarDetailHeader';
import SidebarFooter from '../SidebarFooter';
import Slider from '../../Form/Slider';
import SlopeFilter from './SlopeFilter';
import styles from './TerrainTools.module.scss';
import ghAnalytics from '../../../lib/analytics';

/*
  #NOTE: Since the filters depend on the global values, we can't really separate
  the components as nicely as we'd want. We have to have the changes and pixelFilter
  itself controlled by the parent of all the filters. Makes this file a bit big, but
  it's manageable.
  -- TJC - September 28, 2021
*/
export function TerrainTools(props) {
  const [isOnlyFlat, setIsOnlyFlat] = useState(false)
  useEffect(() => {
    if (props.elevationLayer) {
      props.elevationLayer.pixelFilter = maskPixels;
      props.elevationLayer.opacity = props.opacity;
    }
    if (JSON.stringify(props.aspectValues) == JSON.stringify([9])) {
      setIsOnlyFlat(true)
    } else {
      setIsOnlyFlat(false)
    }
    props.setTerrainValues(props.terrainAnalysis)
  }, [props.aspectValues, props.elevationValues, props.slopeValues]);

  const onChangeAspect = (values) => {
    props.setAspectValues(values);
    props.elevationLayer.redraw();
  }

  const onChangeElevation = (values, thumbIdx) => {
    const newValue = values.join(',')
    const oldValue = props.elevationValues.join(',')
    if (newValue !== oldValue) {
      props.setElevationValues(values);
      props.elevationLayer.redraw();
    }
  };

  const onChangeOpacity = (value, idx) => {
    if (value !== props.opacity) {
      props.setOpacityValue(value);
      props.elevationLayer.opacity = value;
    }
  };

  const onChangeSlope = (values) => {
    props.setSlopeValues(values);
    props.elevationLayer.redraw();
  };

  const resetFilters = () => {
    props.resetFilters();
    props.elevationLayer.redraw();
  };

  const toggleTerrainTools = (e) => {
    const role = props.userProfile.roles[0];
    if (LIMITED_ACCESS_ROLES.includes(role)) {
      props.toggleUserPlanModal(props)
    } else if (e) {
      props.elevationLayer.visible = true;
      props.enableTerrainAnalysis();
      ghAnalytics.logTerrainAnalysisEnabled(); // For Analytics
    } else {
      props.elevationLayer.visible = false;
      props.disableTerrainAnalysis();
      ghAnalytics.logTerrainAnalysisDisabled(); // For Analytics
    }
  };

  const maskPixels = (pixelData) => {
    if (pixelData == null || pixelData.pixelBlock == null) {
      return;
    }

    const elevationValues = props.elevationValues.map(ev => ev / 100)

    // layer is loaded get the min and max temp values from the layer
    if (elevationValues[0] == null || elevationValues[1] == null) {
      props.elevationLayer.redraw();
    }

    // The pixelBlock stores the values of all pixels visible in the view
    const pixelBlock = pixelData.pixelBlock;
    // The pixels visible in the view
    const pixels = pixelBlock.pixels;
    let mask = pixelBlock.mask;

    // The number of pixels in the pixelBlock
    const numPixels = pixelBlock.width * pixelBlock.height;

    if (pixels == null) {
      return;
    }

    // Get the pixels containing elevation values in the only band of the data
    const aspectBand = pixels[TERRAIN_BANDS.Aspect];
    const elevBand = pixels[TERRAIN_BANDS.Elevation];
    const slopeBand = pixels[TERRAIN_BANDS.Slope];

    // Create empty arrays for each of the RGB bands to set on the pixelBlock
    const rBand = new Uint8Array(elevBand.length);
    const gBand = new Uint8Array(elevBand.length);
    const bBand = new Uint8Array(elevBand.length);

    if (mask == null) {
      mask = new Uint8Array(elevBand.length); //mask = new Uint8Array(p1.length);
      mask.fill(1);
      pixelBlock.mask = mask;
    }
    // Loop through all the pixels in the view
    let inAspect = true;
    let inElevation = true;
    let inSlope = true;
    for (let i = 0; i < numPixels; i++) {
      // skip noData pixels
      if (mask[i] === 0) {
        continue;
      }

      inAspect = props.aspectValues.includes(aspectBand[i]);
      inElevation = elevBand[i] >= elevationValues[0] && elevBand[i] <= elevationValues[1];
      inSlope = props.slopeValues.includes(slopeBand[i]) || isOnlyFlat;
      mask[i] = inElevation && inSlope && inAspect ? 1 : 0;

      //apply color based on temperature value of each pixel
      if (mask[i]) {
        rBand[i] = 0;
        gBand[i] = 255;
        bBand[i] = 0;
      }
    }

    // Set the new pixel values on the pixelBlock
    pixelData.pixelBlock.pixels = [rBand, gBand, bBand]; //assign rgb values to each pixel
    // pixelData.pixelBlock.statistics = null;
    pixelData.pixelBlock.pixelType = 'u8';
  };

  return (
    <>
      <SidebarDetailHeader
        title='Terrain Analysis'
      />
      <SidebarBody className={styles.sidebarBody}>
        <div className={styles.topSection}>
          <div className={styles.topControls}>
            <Slider
              disabled={!props.toolEnabled}
              max={1}
              min={0}
              // onChange={props.is3d ? noop : onChangeOpacity}
              onAfterChange={onChangeOpacity}
              step={0.01}
              value={[props.opacity]}
            />
            <Switch
              checked={props.toolEnabled}
              checkedIcon={false}
              height={24}
              offColor='#333333'
              offHandleColor='#ffffff'
              onChange={toggleTerrainTools}
              onColor='#f37735'
              onHandleColor='#ffffff'
              uncheckedIcon={false}
              width={40}
            />
          </div>
          <p className={styles.transparencyLabel}>Transparency</p>
          <p className={styles.sidebarInfo}>
            Analyze terrain to find areas suitable for game at any time of the
            year. Use the filters below to narrow your search for favorable
            terrain based on elevation, slope, and aspect.
          </p>
        </div>
        <ElevationPanel
          elevationValues={props.elevationValues}
          onChange={onChangeElevation}
        />
        <SlopeFilter
          isOnlyFlat={isOnlyFlat}
          onChange={onChangeSlope}
          slopeValues={props.slopeValues}
        />
        <AspectFilter
          aspectValues={props.aspectValues}
          onChange={onChangeAspect}
        />
      </SidebarBody>
      <SidebarFooter>
        <div className={styles.resetButton}>
          <Button
            disabled={!props.toolEnabled}
            onClick={resetFilters}
            theme={Button.THEME.outlinePrimary}
          >
            Reset all
          </Button>
        </div>
      </SidebarFooter>
    </>
  );
}

TerrainTools.propTypes = {
  aspectValues: PropTypes.array,
  elevationLayer: PropTypes.any,
  elevationValues: PropTypes.array,
  is3d: PropTypes.bool,
  opacity: PropTypes.number,
  slopeValues: PropTypes.array,
  terrainAnalysis: PropTypes.object,
  toolEnabled: PropTypes.bool,
  userProfile: PropTypes.object,
  //actions
  disableTerrainAnalysis: PropTypes.func,
  enableTerrainAnalysis: PropTypes.func,
  resetFilters: PropTypes.func,
  setAspectValues: PropTypes.func,
  setElevationValues: PropTypes.func,
  setOpacityValue: PropTypes.func,
  setSlopeValues: PropTypes.func,
  setTerrainValues: PropTypes.func,
  toggleUserPlanModal: PropTypes.func
};

export default connect(
  (model) => {
    const { map, terrain: { preferences } } = model
    return {
      aspectValues: preferences.terrainAnalysis.aspect,
      elevationLayer: map.mapRef.findLayerById(MAP_LAYER_IDS.terrainAnalysis),
      elevationValues: preferences.terrainAnalysis.elevation,
      is3d: map.mapMode === MAP_MODES.ThreeD,
      opacity: preferences.terrainAnalysis.opacity,
      slopeValues: preferences.terrainAnalysis.slope,
      terrainAnalysis: preferences.terrainAnalysis,
      toolEnabled: preferences.terrainAnalysis.enabled,
      userProfile: model.user.profile
    }
  },
  actions
)(TerrainTools);

